import asyncSeries from 'async/series';
import asyncParallel from 'async/parallel';
import React from 'react';
import EventEmitter from 'events';

import Api from '../tools/api';
import Storage from '../tools/storage';
import util from '../tools/util';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isLoggedIn,
  isReady,
  isSuperAdmin,
  getUser,
  useIsLoggedIn,
  useUser,
  useIsReady,
  verifySessionKey,
  emailCode,
  loginCode,
  logout,
  get,
  post,
  del,
};

const SESSION_KEY = 'ARES_ADMIN_SESSION_KEY';
const CUSTOM_REGEX = /^([^.]*)\.local\.ares-dev\.com$|^([^.]*)\.maxlo\.cloud$/i;

let g_isReady = false;
let g_sessionKey: string | null = null;
let g_user: any = null;
const HOST_MATCH = window.location.hostname.match(CUSTOM_REGEX);
const g_customDomain = HOST_MATCH?.[1] ?? HOST_MATCH?.[2] ?? null;

const g_eventEmitter = new EventEmitter();
g_eventEmitter.setMaxListeners(20);
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function isReady() {
  return g_isReady;
}
function isLoggedIn() {
  return Boolean(g_user);
}
function isSuperAdmin() {
  return Boolean(g_user && g_user.is_super_admin);
}
export function getCustomDomain() {
  return g_customDomain;
}
function getUser() {
  return g_user;
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useUser() {
  return React.useSyncExternalStore(_subscribe, getUser);
}
export function useIsLoggedIn() {
  return React.useSyncExternalStore(_subscribe, isLoggedIn);
}
export function useIsReady() {
  return React.useSyncExternalStore(_subscribe, isReady);
}
function init(done) {
  asyncSeries(
    [
      _load,
      (done) => {
        if (g_sessionKey) {
          verifySessionKey({}, () => done());
        } else {
          done();
        }
      },
    ],
    (err) => {
      done(err);
    }
  );
}
function verifySessionKey(params, done) {
  if (g_sessionKey) {
    const opts: any = {
      url: '/api/1/user/current',
    };
    if (params?.retry) {
      opts.retry = true;
      opts.retry_times = 9999;
    }
    get(opts, (err, body) => {
      if (err === 403) {
        g_user = null;
        g_sessionKey = null;
        g_isReady = true;
        _emit('not_logged_in');
        _save();
      } else if (err) {
        util.errorLog('UserStore.verifySessionKey: err:', err);
      } else if (!body.is_admin) {
        g_user = null;
        g_sessionKey = null;
        g_isReady = true;
        _emit('not_logged_in');
        _save();
        err = 'not_admin';
      } else {
        g_user = body;
        g_isReady = true;
        _emit('user_login');
      }
      done(err);
    });
  } else {
    g_user = null;
    g_sessionKey = null;
    g_isReady = true;
    _emit('not_logged_in');
    _save();
    done('not_logged_in');
  }
}
function emailCode(params: any, done: (err: string | number) => void) {
  const { email } = params;
  const opts = {
    url: '/api/1/user/email_code',
    body: { email },
  };
  Api.post(opts, (err: any, body: any) => {
    if (err) {
      util.errorLog('UserStore.emailCode: err:', err, body);
    }
    done(err);
  });
}
function loginCode(
  params: any,
  done: (err: string | number, user?: any) => void
) {
  const { email, login_code } = params;
  const req_body: any = {
    email,
    login_code: login_code.replace(/[^0-9]/g, ''),
  };
  const opts = {
    url: '/api/1/user/email_login',
    body: req_body,
  };
  Api.post(opts, (err: any, body: any) => {
    if (err) {
      util.errorLog('UserStore.login: err:', err, body);
      done(err);
    } else {
      g_sessionKey = body.user_session_key;
      _save();
      verifySessionKey({}, done);
    }
  });
}
function logout(done: (err?: string) => void) {
  const opts = {
    url: '/api/1/user/logout',
  };
  post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserStore.logout: err:', err, body);
    }
    g_sessionKey = null;
    g_user = null;
    _save();
    _emit('logout');
    done(err);
  });
}
function get(opts, done) {
  _request(opts, 'GET', done);
}
function post(opts, done) {
  _request(opts, 'POST', done);
}
function del(opts, done) {
  _request(opts, 'DELETE', done);
}
function _load(done) {
  asyncParallel(
    [
      (done) =>
        Storage.getItem(SESSION_KEY, (err, data) => {
          if (!err && data) {
            g_sessionKey = data;
          }
          done();
        }),
    ],
    done
  );
}
function _save() {
  Storage.setItem({ key: SESSION_KEY, value: g_sessionKey });
}
function _request(opts, method, done) {
  if (typeof opts === 'string') {
    opts = {
      url: opts,
    };
  }
  opts.method = method;
  if (!opts.query) {
    opts.query = {};
  }
  opts.query.user_session_key = g_sessionKey;
  Api.request(opts, done);
}
